import type {
  CoachingGoal,
  CoachingGoalReflection,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { asAsyncBelongsTo } from '@efdigital/ll-xceed-v2/data/async-setter';
import { createCoachingGoalReflection } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goal-reflections';
import { reflectionsForGoal } from '@efdigital/ll-xceed-v2/utils/coaching/goal-children';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface GoalReflectionsSignature {
  Args: {
    goal: CoachingGoal;
    reflections: readonly CoachingGoalReflection[];
    role: 'coach' | 'coachee';
    onSaved: () => Promise<void>;
  };
  Element: HTMLDivElement;
}

function formatSavedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default class GoalReflections extends Component<GoalReflectionsSignature> {
  @service declare store: Store;

  @tracked draft = '';
  @tracked saving = false;
  @tracked errorMessage: string | null = null;

  get notes(): CoachingGoalReflection[] {
    return reflectionsForGoal(this.args.reflections, this.args.goal.id!);
  }

  get canAdd(): boolean {
    return this.draft.trim().length > 0;
  }

  get addDisabled(): boolean {
    return !this.canAdd || this.saving;
  }

  get bodyFieldId(): string {
    return `goal-reflection-${this.args.goal.id}-body`;
  }

  setDraft = (event: Event) => {
    this.draft = (event.target as HTMLTextAreaElement).value;
  };

  addNote = async (event: Event) => {
    event.preventDefault();
    if (!this.canAdd || this.saving) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    const reflection = this.store.createRecord<CoachingGoalReflection>(
      DataTypeCoaching.CoachingGoalReflection,
      {
        coachingGoal: asAsyncBelongsTo(this.args.goal),
        body: this.draft.trim(),
      }
    );

    try {
      await this.store.request(
        createCoachingGoalReflection(reflection, this.args.goal)
      );
      this.draft = '';
      await this.args.onSaved();
    } catch (error) {
      reflection.unloadRecord();
      console.error(error);
      this.errorMessage =
        'The reflection could not be saved. Please try again.';
    } finally {
      this.saving = false;
    }
  };

  savedAt = (note: CoachingGoalReflection): string =>
    formatSavedAt(note.createdAt);

  authorLabel = (note: CoachingGoalReflection): string | null => {
    if (!note.createdByUserId) {
      return null;
    }

    const byCoachee = note.createdByUserId === this.args.goal.user;

    if (this.args.role === 'coach') {
      return byCoachee ? 'Coachee' : 'You';
    }

    return byCoachee ? 'You' : 'Coach';
  };

  <template>
    <div
      class="card border shadow-sm rounded-4 overflow-hidden"
      data-test-goal-reflections
      ...attributes
    >
      <div class="card-body p-4">
        <h2 class="gud-eyebrow text-body-secondary mb-2">Reflection</h2>
        <p class="small text-body-secondary mb-3">Add notes on how this goal is
          going — progress, blockers, or what you learned.</p>

        {{#if this.notes.length}}
          <ul class="list-unstyled mb-3">
            {{#each this.notes key="id" as |note|}}
              <li class="bg-body rounded-3 p-3 mb-2" data-test-goal-reflection>
                <p class="mb-2">{{note.body}}</p>
                <small class="text-body-secondary">
                  {{#if (this.authorLabel note)}}
                    {{this.authorLabel note}}
                    ·
                  {{/if}}
                  {{this.savedAt note}}
                </small>
              </li>
            {{/each}}
          </ul>
        {{/if}}

        <form {{on "submit" this.addNote}}>
          <label for={{this.bodyFieldId}} class="form-label">Add a note</label>
          <textarea
            id={{this.bodyFieldId}}
            class="form-control"
            rows="3"
            maxlength="5000"
            placeholder="What did you try? What will you do next?"
            value={{this.draft}}
            {{on "input" this.setDraft}}
          ></textarea>
          {{#if this.errorMessage}}
            <p
              class="text-danger small mt-2 mb-0"
              role="alert"
              data-test-goal-reflection-error
            >
              {{this.errorMessage}}
            </p>
          {{/if}}
          <button
            type="submit"
            class="btn btn-outline-primary rounded-pill mt-3"
            disabled={{this.addDisabled}}
            data-test-goal-reflection-add
          >
            <iconify-icon inline icon="ph:plus" aria-hidden="true" />
            {{if this.saving "Saving…" "Add reflection"}}
          </button>
        </form>
      </div>
    </div>
  </template>
}
